import { Injectable, inject, signal, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { tap, catchError, switchMap, finalize } from 'rxjs/operators';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  private apiUrl = `${environment.apiUrl}`;

  // Store authentication state globally
  currentUser = signal<any>(null);
  isAuthenticated = signal<boolean>(false);
  /** Token present but /users/me not yet completed or never called */
  userLoading = signal<boolean>(false);

  // BehaviorSubject to track refresh state
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor() {
    // checkInitialState is called in browser via APP_INITIALIZER (fetch on reload)
  }

  /** Called when app opens in browser; if token exists, requests /users/me */
  init(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      return this.checkInitialState();
    }
    return Promise.resolve();
  }

  private checkInitialState(): Promise<void> {
    const token = this.getAccessToken();
    if (!token) return Promise.resolve();

    this.isAuthenticated.set(true);
    this.userLoading.set(true);

    return new Promise<void>((resolve) => {
      this.fetchCurrentUser().subscribe({
        next: () => resolve(),
        error: (err) => {
          // On 401/403: clear tokens — guard will redirect to /login
          if (err?.status === 401 || err?.status === 403) {
            this.clearTokens();
          }
          // On other errors (network, 5xx etc.) keep token and stay on page
          resolve();
        }
      });
    });
  }

  getAccessToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('access_token');
    }
    return null;
  }

  getRefreshToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('refresh_token');
    }
    return null;
  }

  private setTokens(access: string, refresh: string) {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
    }
    this.isAuthenticated.set(true);
  }

  private clearTokens() {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
    this.isAuthenticated.set(false);
    this.currentUser.set(null);
  }

  login(credentials: unknown): Observable<unknown> {
    return this.http.post<any>(`${this.apiUrl}/auth/login`, credentials).pipe(
      switchMap((res) => {
        if (res?.access_token && res?.refresh_token) {
          this.setTokens(res.access_token, res.refresh_token);
          return this.fetchCurrentUser();
        }
        const detail = res?.detail ?? res?.message ?? 'Invalid email or password.';
        return throwError(() => ({ status: 400, error: { detail } }));
      })
    );
  }

  register(userData: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/register`, userData);
  }

  logout() {
    const refresh_token = this.getRefreshToken();
    this.http.post(`${this.apiUrl}/auth/logout`, { refresh_token }).pipe(
      catchError(() => {
        return throwError(() => new Error('Logout failed on server, continuing local logout'));
      })
    ).subscribe({
      next: () => this.handleLocalLogout(),
      error: () => this.handleLocalLogout()
    });
  }

  private handleLocalLogout() {
    this.clearTokens();
    this.router.navigate(['/login']);
  }

  refreshTokens(): Observable<any> {
    const refresh_token = this.getRefreshToken();
    if (!refresh_token) {
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http.post(`${this.apiUrl}/auth/refresh`, { refresh_token }).pipe(
      tap((tokens: any) => {
        if (tokens.access_token && tokens.refresh_token) {
          this.setTokens(tokens.access_token, tokens.refresh_token);
          this.refreshTokenSubject.next(tokens.access_token);
        }
      }),
      catchError((error) => {
        this.clearTokens();
        this.router.navigate(['/login']);
        return throwError(() => error);
      })
    );
  }

  fetchCurrentUser(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/users/me`).pipe(
      tap((user: any) => {
        this.currentUser.set(user);
        if (user?.force_password_reset === true) {
          this.router.navigate(['/change-password']);
        }
      }),
      finalize(() => this.userLoading.set(false)),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
